import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Navbar } from '../../components/navbar'
import { Footer } from '../../components/footer'
import { V1SimplifiedEdit } from '../../submission/components/deal-submission/v1-simplified-edit'
import {
  fetchDealFormData,
  fetchDealById,
  updateDeal,
  deepMergeDealPayload,
} from '../../submission/lib/deal-api'
import type { DealFormData } from '../../submission/lib/types'

const EditDealSummaryPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  const [formData, setFormData] = useState<DealFormData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)

  useEffect(() => {
    let isMounted = true
    if (!projectId) {
      setLoadError('Missing deal identifier.')
      setIsLoading(false)
      setFormData(null)
      return
    }
    setIsLoading(true)
    setLoadError(null)

    fetchDealFormData(projectId)
      .then((data) => {
        if (!isMounted) return
        setFormData(data)
      })
      .catch((error) => {
        console.error('Failed to load deal details', error)
        if (!isMounted) return
        setLoadError('Unable to load deal details. Please try again later.')
        setFormData(null)
      })
      .finally(() => {
        if (isMounted) setIsLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [projectId])

  const handleUpdate = useCallback(
    async (payload: any) => {
      if (!projectId) throw new Error('Deal identifier is missing.')

      // Re-fetch the live deal so we merge onto the latest server state.
      const existingDeal = await fetchDealById(projectId)

      // This page is only allowed to change the deal's `overview` section
      // (project name, logo, banner, URLs, project overview, sale details)
      // and its slug.
      // Everything else is copied verbatim from the freshly-fetched server
      // record so it is guaranteed to remain untouched:
      //   - all other top-level fields (id, state, status, approved,
      //     is_visible, created_at, ...)
      //   - all other metadata sections (detail, funds, timelines,
      //     nftMetadata, visibilityEligibility, ...)
      const incomingOverview = payload?.metadata?.overview ?? {}
      const existingMetadata = existingDeal?.metadata ?? {}
      const existingOverview = existingMetadata.overview ?? {}

      const merged = {
        ...existingDeal,
        ...(payload?.slug ? { slug: payload.slug } : {}),
        metadata: {
          ...existingMetadata,
          overview: deepMergeDealPayload(existingOverview, incomingOverview),
        },
      }

      await updateDeal(projectId, merged)
    },
    [projectId],
  )

  return (
    <div className="flex flex-col min-h-screen bg-neutral-white">
      <Navbar />
      <main className="flex-1 flex flex-col">
        {formData && projectId && (
          <V1SimplifiedEdit
            formData={formData}
            dealId={projectId}
            onSubmit={handleUpdate}
            isLoading={isLoading}
            includeSaleContract={false}
          />
        )}
        {loadError && !isLoading && (
          <div className="max-w-3xl mx-auto w-full px-4 pb-12">
            <div className="rounded-md border border-status-error/40 bg-red-50 p-4 text-sm text-status-error">
              {loadError}
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  )
}

export default EditDealSummaryPage
